/**
 * StepStaticResourcesEditor - Manage static resources from manifest
 *
 * Reads static resource requirements from plugin manifest (type: "static")
 * and provides UI to configure them. These will be available in funnel
 * context under context.static.
 *
 * Difference from StepResourcesEditor:
 * - StepResourcesEditor: Reads from config schema (design-time config)
 * - StepStaticResourcesEditor: Reads from manifest requirements (funnel-time static data)
 */

import {
  OrderBumpSelector,
  PriceSelector,
  ProductSelector,
  VariantSelector,
} from '@/components/resource-pickers';
import { OrderBumpsProvider } from '@/components/config-editor/providers/order-bumps-provider';
import { UpsellsProvider } from '@/components/config-editor/providers/upsells-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchPriceInput } from '@/components/form/search-price-input';
import { SearchPromotionInput } from '@/components/form/search-promotion-input';
import { trpc } from '@/lib/api/trpc';
import { useCreateOffer, useOffer, useOffersSearch } from '@/lib/api/offers';
import { usePrice } from '@/lib/api/prices';
import { useProduct } from '@/lib/api/products';
import { usePromotion } from '@/lib/api/promotions';
import { formatMoney } from '@/lib/money';
import { cn } from '@/lib/utils';
import { canRemoveOffer, removeOffer, replaceOffer } from '../utils/offerBindings';
import { Check, ChevronRight, Database, ImageOff, Loader2, Package, Plus, Search, Tag, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StaticResource {
  resource: string; // e.g., "offer", "product", "variant"
  label: string;
  type: 'product' | 'variant' | 'price' | 'offer' | 'orderBump';
  required: boolean;
  description?: string;
  // Multi-binding support — when true the resource holds an array of IDs
  // and the UI renders N cards plus an "Add" button. Defaults to false so
  // existing single-binding plugins are untouched.
  multiple?: boolean;
  /** When `multiple` is true: minimum required (defaults to 1). */
  min?: number;
  /** When `multiple` is true: maximum allowed (defaults to unbounded). */
  max?: number;
}

interface StepStaticResourcesEditorProps {
  stepId: string;
  variantId?: string; // For split steps, resources are per variant
  funnelId: string;
  instanceId?: string;
  storeId: string;
  pagePath?: string; // The internal path of the current page (e.g., "/checkout", "/offer")
  // Controlled component: receives current values and notifies on change
  staticResources: Record<string, any>; // Can be string (legacy), {id: string}, or string[] (multi)
  onResourceChange: (resourceType: string, resourceValue: string | { id: string } | string[]) => void;
}

/**
 * Convert resource name to user-friendly label
 */
function generateLabel(resource: string): string {
  const labels: Record<string, string> = {
    product: 'Product',
    variant: 'Variant',
    price: 'Price',
    offer: 'Offer',
    orderBump: 'Order Bump',
  };
  return labels[resource] || resource;
}

/**
 * Detect resource type from manifest requirement
 * Supports both explicit resourceType field and name-based detection
 */
function detectResourceType(requirement: any): StaticResource['type'] | null {
  // First check explicit resourceType field (new format)
  if (requirement.resourceType) {
    const validTypes: StaticResource['type'][] = ['product', 'variant', 'price', 'offer', 'orderBump'];
    if (validTypes.includes(requirement.resourceType)) {
      return requirement.resourceType;
    }
  }

  // Fallback to name-based detection for backward compatibility
  const resource = requirement.resource;
  if (!resource) return null;

  const resourceLower = resource.toLowerCase();
  const mapping: Record<string, StaticResource['type']> = {
    product: 'product',
    variant: 'variant',
    price: 'price',
    offer: 'offer',
    orderbump: 'orderBump',
    orderBump: 'orderBump',
  };

  // Exact match first
  if (mapping[resourceLower]) {
    return mapping[resourceLower];
  }

  // Pattern matching for suffixed/prefixed names (e.g., bundle1, variant2, primaryOffer)
  for (const [key, type] of Object.entries(mapping)) {
    if (resourceLower.includes(key)) {
      return type;
    }
  }

  return null;
}

/**
 * Parse manifest requirements to extract static resources
 * @param manifest - The manifest object with pages
 * @param filterByPagePath - Optional page path to filter resources for a specific page
 */
function parseManifestRequirements(manifest: any, filterByPagePath?: string): StaticResource[] {
  const staticResources: StaticResource[] = [];

  if (!manifest?.pages) {
    return staticResources;
  }

  // Collect all unique static resources from matching pages
  const uniqueResources = new Map<string, StaticResource>();

  manifest.pages.forEach((page: any) => {
    // If filtering by page path, skip non-matching pages
    if (filterByPagePath && page.path !== filterByPagePath) {
      return;
    }

    page.features?.forEach((feature: any) => {
      feature.requirements?.forEach((req: any) => {
        // Support both 'from' (new format) and 'params' (legacy format)
        const paramList = req.from || req.params || [];

        // Check if this requirement has static params
        const hasStatic = paramList.some((param: any) => param.type === 'static');

        if (hasStatic && req.resource) {
          const resourceType = detectResourceType(req);
          if (resourceType) {
            const key = req.resource;
            if (!uniqueResources.has(key)) {
              uniqueResources.set(key, {
                resource: req.resource,
                // Prefer explicit label from manifest, fallback to generated
                label: req.label || generateLabel(req.resource),
                type: resourceType,
                // Default to required: true unless explicitly set to false
                required:
                  paramList.some((p: any) => p.type === 'static' && p.required !== false) &&
                  req.optional !== true,
                // Use description from manifest or default
                description: req.description || `Static ${req.resource} configured for this step`,
                // Multi-binding flags from manifest. `multiple` defaults to
                // false so legacy single-binding plugins continue to work
                // unchanged.
                multiple: req.multiple === true,
                min: typeof req.min === 'number' ? req.min : undefined,
                max: typeof req.max === 'number' ? req.max : undefined,
              });
            }
          }
        }
      });
    });
  });

  const result = Array.from(uniqueResources.values());
  return result;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function getOfferTitle(offer: any): string {
  if (offer?.titleTrans?.en) return offer.titleTrans.en;
  if (offer?.titleTrans) {
    const keys = Object.keys(offer.titleTrans);
    if (keys.length > 0) return offer.titleTrans[keys[0]];
  }
  return `Offer ${offer?.type || ''}`.trim();
}

function getOfferPriceInfo(offer: any): { amount: number; currency: string } | null {
  const lineItems = offer?.offerLineItems || [];
  let total = 0;
  let currency = '';
  for (const li of lineItems) {
    const co = li.price?.currencyOptions;
    if (co) {
      const cur = Object.keys(co)[0];
      if (cur && typeof co[cur]?.amount === 'number') {
        total += co[cur].amount * (li.quantity || 1);
        if (!currency) currency = cur.toUpperCase();
      }
    }
  }
  return total > 0 ? { amount: total, currency } : null;
}

// ─── Selected Offer Card ─────────────────────────────────────────────

function SelectedOfferCard({
  offer,
  onClear,
}: {
  offer: any;
  onClear: () => void;
}) {
  const { t } = useTranslation('storefront');
  const title = getOfferTitle(offer);
  const priceInfo = getOfferPriceInfo(offer);
  const lineItems = offer?.offerLineItems || [];
  const firstVariant = lineItems[0]?.price?.variant;
  const imageUrl = firstVariant?.imageUrl;
  const productName = firstVariant?.product?.name;

  return (
    <div className={cn(
      'flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-all',
      'border-primary/40 bg-primary/5',
    )}>
      <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="h-full w-full object-cover" />
        ) : (
          <Package className="text-muted-foreground/50 h-3.5 w-3.5" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-xs font-medium">{title}</span>
          {priceInfo && (
            <Badge variant="secondary" className="shrink-0 text-[10px] font-semibold tabular-nums">
              {formatMoney(priceInfo.amount, priceInfo.currency)}
            </Badge>
          )}
        </div>
        {productName && (
          <p className="text-muted-foreground truncate text-[10px]">
            {productName}
            {lineItems.length > 1 ? ` + ${t('stepStaticResources.moreItems', { count: lineItems.length - 1 })}` : ''}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onClear}
        className="text-muted-foreground hover:bg-muted hover:text-foreground shrink-0 rounded-md p-1 transition-colors"
        title={t('stepStaticResources.changeOffer')}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Compact Offer Picker ────────────────────────────────────────────

function CompactOfferPicker({
  value,
  onChange,
  storeId,
  excludeIds,
}: {
  value: string;
  onChange: (value: string) => void;
  storeId: string;
  // Offer ids to omit from the search result list — used by the multi-
  // offer flow so already-added packs don't show up as choices when the
  // admin opens the "Add offer" picker for the next slot.
  excludeIds?: string[];
}) {
  const { t } = useTranslation('storefront');
  const [isOpen, setIsOpen] = React.useState(!value);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [showCreateForm, setShowCreateForm] = React.useState(false);

  const searchTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setDebouncedSearch(q), 300);
  }, []);

  const { data: selectedOfferData } = useOffer(value || '', !!value);
  const shouldSearch = !!debouncedSearch.trim();
  const { data: allData, isLoading: loadingAll } = useOffersSearch(
    { storeId, search: '' },
    !!storeId && !shouldSearch,
  );
  const { data: searchData, isLoading: loadingSearch } = useOffersSearch(
    { storeId, search: debouncedSearch },
    !!storeId && shouldSearch,
  );

  const excludeSet = React.useMemo(
    () => new Set(excludeIds || []),
    [excludeIds],
  );

  const offers = React.useMemo(() => {
    const raw = shouldSearch ? (searchData?.offers || []) : (allData?.offers || []);
    // Strip already-bound ids unless the current value itself is one of
    // them (single-binding mode pins the active selection at the top).
    const filtered = raw.filter((o: any) => !excludeSet.has(o.id) || o.id === value);
    if (value && selectedOfferData?.offer) {
      const existing = filtered.find((o: any) => o.id === value);
      if (!existing) return [selectedOfferData.offer, ...filtered].slice(0, 50);
      const others = filtered.filter((o: any) => o.id !== value);
      return [existing, ...others].slice(0, 50);
    }
    return filtered.slice(0, 50);
  }, [shouldSearch, searchData, allData, value, selectedOfferData, excludeSet]);

  const loading = shouldSearch ? loadingSearch : loadingAll;
  // Count before `excludeSet` is applied: an empty list means something
  // different to the admin depending on whether the store has no offers at
  // all or every one of them is already bound to this step.
  const rawCount = (shouldSearch ? searchData?.offers : allData?.offers)?.length ?? 0;

  const handleSelect = (offerId: string) => {
    onChange(offerId);
    setIsOpen(false);
    setSearchQuery('');
    setDebouncedSearch('');
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(true);
  };

  // Show selected offer card when collapsed
  if (!isOpen && value && selectedOfferData?.offer) {
    return (
      <SelectedOfferCard
        offer={selectedOfferData.offer}
        onClear={handleClear}
      />
    );
  }

  // Show selected offer card with loading state
  if (!isOpen && value && !selectedOfferData?.offer) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border px-3 py-2">
        <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />
        <span className="text-muted-foreground text-xs">{t('stepStaticResources.loadingOffer')}</span>
        <button
          type="button"
          onClick={handleClear}
          className="text-muted-foreground hover:text-foreground ml-auto rounded-md p-1"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Search + Create row */}
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            type="text"
            placeholder={t('stepStaticResources.searchOffers')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="h-8 pl-8 pr-7 text-sm"
            autoFocus={!value}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => { setSearchQuery(''); setDebouncedSearch(''); }}
              className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1 px-2.5 text-xs"
          onClick={() => setShowCreateForm(true)}
        >
          <Plus className="h-3 w-3" />
          {t('stepStaticResources.new')}
        </Button>
      </div>

      {/* Inline create form */}
      {showCreateForm && (
        <InlineOfferCreateForm
          storeId={storeId}
          onCreated={(offerId) => {
            setShowCreateForm(false);
            handleSelect(offerId);
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* Offer list */}
      {!showCreateForm && (
        <div className={cn(
          'space-y-1 overflow-y-auto',
          offers.length > 4 && 'max-h-[280px]',
        )}>
          {loading && offers.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          ) : offers.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-6 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                <Package className="text-muted-foreground h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium">
                  {debouncedSearch
                    ? t('stepStaticResources.noOffersMatching', { query: debouncedSearch })
                    : rawCount > 0
                      ? t('stepStaticResources.allOffersAdded')
                      : t('stepStaticResources.noOffersYet')}
                </p>
                {!debouncedSearch && (
                  <p className="text-[10px] text-muted-foreground">
                    {rawCount > 0
                      ? t('stepStaticResources.allOffersAddedHint')
                      : t('stepStaticResources.createOfferHint')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            offers.map((offer: any) => {
              const title = getOfferTitle(offer);
              const priceInfo = getOfferPriceInfo(offer);
              const firstVariant = offer.offerLineItems?.[0]?.price?.variant;
              const imageUrl = firstVariant?.imageUrl;
              const productName = firstVariant?.product?.name;
              const isSelected = value === offer.id;

              return (
                <button
                  key={offer.id}
                  type="button"
                  onClick={() => handleSelect(offer.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
                    isSelected
                      ? 'border-primary/40 bg-primary/5'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border">
                    {imageUrl ? (
                      <img src={imageUrl} alt={title} className="h-full w-full object-cover" />
                    ) : (
                      <ImageOff className="text-muted-foreground/40 h-3.5 w-3.5" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">{title}</p>
                    {productName && (
                      <p className="text-muted-foreground truncate text-[10px]">{productName}</p>
                    )}
                  </div>
                  {priceInfo && (
                    <Badge variant="secondary" className="shrink-0 text-[10px] font-semibold tabular-nums">
                      {formatMoney(priceInfo.amount, priceInfo.currency)}
                    </Badge>
                  )}
                  {isSelected && (
                    <Check className="text-primary h-3.5 w-3.5 shrink-0" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inline Create Form ──────────────────────────────────────────────

type InlineLineItem = {
  priceId: string;
  productId: string;
  variantId: string;
  quantity: number;
};

type InlineOfferForm = {
  titleEn: string;
  type: 'flash' | 'bundle' | 'club';
  lineItems: InlineLineItem[];
  promotionIds: string[];
};

const INITIAL_OFFER_FORM: InlineOfferForm = {
  titleEn: '',
  type: 'club',
  lineItems: [],
  promotionIds: [],
};

function InlineOfferCreateForm({
  storeId,
  onCreated,
  onCancel,
}: {
  storeId: string;
  onCreated: (offerId: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('storefront');
  const [form, setForm] = React.useState<InlineOfferForm>(INITIAL_OFFER_FORM);
  const [showAddProduct, setShowAddProduct] = React.useState(false);
  const createMutation = useCreateOffer();

  const isValid = form.titleEn.trim() && form.lineItems.length > 0;

  const handleAddLineItem = (selection: { priceId: string; productId: string; variantId: string }) => {
    if (form.lineItems.some((li) => li.priceId === selection.priceId)) return;
    setForm((f) => ({
      ...f,
      lineItems: [...f.lineItems, { ...selection, quantity: 1 }],
    }));
    setShowAddProduct(false);
  };

  const handleRemoveLineItem = (index: number) => {
    setForm((f) => ({
      ...f,
      lineItems: f.lineItems.filter((_, i) => i !== index),
    }));
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    setForm((f) => ({
      ...f,
      lineItems: f.lineItems.map((li, i) => i === index ? { ...li, quantity: Math.max(1, quantity) } : li),
    }));
  };

  const handlePriceChange = (index: number, priceId: string, variantId: string) => {
    setForm((f) => ({
      ...f,
      lineItems: f.lineItems.map((li, i) => i === index ? { ...li, priceId, variantId } : li),
    }));
  };

  const handleCreate = async () => {
    if (!isValid) return;
    try {
      const result = await createMutation.mutateAsync({
        storeId,
        type: form.type,
        titleTrans: { en: form.titleEn.trim() },
        lineItems: form.lineItems.map((li) => ({ priceId: li.priceId, quantity: li.quantity })),
        promotionIds: form.promotionIds.length > 0 ? form.promotionIds : undefined,
      });
      if (result?.id) onCreated(result.id);
    } catch {
      // Error handled by mutation hook (toast)
    }
  };

  return (
    <div className="space-y-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide">{t('stepStaticResources.newOffer')}</h4>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Title + Type row */}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <Label className="text-xs">{t('stepStaticResources.title')}</Label>
          <Input
            value={form.titleEn}
            onChange={(e) => setForm((f) => ({ ...f, titleEn: e.target.value }))}
            placeholder={t('stepStaticResources.titlePlaceholder')}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="w-[100px] shrink-0 space-y-1">
          <Label className="text-xs">{t('stepStaticResources.type')}</Label>
          <Select
            value={form.type}
            onValueChange={(v: 'flash' | 'bundle' | 'club') => setForm((f) => ({ ...f, type: v }))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="flash">Flash</SelectItem>
              <SelectItem value="bundle">Bundle</SelectItem>
              <SelectItem value="club">Club</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Line Items */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs">
            {t('stepStaticResources.products')}
            {form.lineItems.length > 0 && (
              <span className="ml-1 text-muted-foreground">({form.lineItems.length})</span>
            )}
          </Label>
          {!showAddProduct && (
            <button
              type="button"
              onClick={() => setShowAddProduct(true)}
              className="flex items-center gap-1 text-[10px] font-medium text-primary hover:text-primary/80"
            >
              <Plus className="h-3 w-3" />
              {t('stepStaticResources.add')}
            </button>
          )}
        </div>

        {showAddProduct && (
          <div className="rounded-md border bg-background p-2">
            <SearchPriceInput
              storeId={storeId}
              onChange={handleAddLineItem}
            />
            <button
              type="button"
              onClick={() => setShowAddProduct(false)}
              className="mt-1 text-[10px] text-muted-foreground hover:text-foreground"
            >
              {t('stepStaticResources.cancel')}
            </button>
          </div>
        )}

        {form.lineItems.length > 0 && (
          <div className="space-y-1">
            {form.lineItems.map((li, idx) => (
              <InlineLineItemCard
                key={li.priceId}
                item={li}
                storeId={storeId}
                onQuantityChange={(q) => handleQuantityChange(idx, q)}
                onPriceChange={(priceId, variantId) => handlePriceChange(idx, priceId, variantId)}
                onRemove={() => handleRemoveLineItem(idx)}
              />
            ))}
          </div>
        )}

        {form.lineItems.length === 0 && !showAddProduct && (
          <button
            type="button"
            onClick={() => setShowAddProduct(true)}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed py-3 text-[10px] text-muted-foreground hover:border-primary/30 hover:text-primary"
          >
            <Package className="h-3 w-3" />
            {t('stepStaticResources.addAtLeastOneProduct')}
          </button>
        )}
      </div>

      {/* Promotions (collapsible) */}
      <InlinePromotionsPicker
        storeId={storeId}
        promotionIds={form.promotionIds}
        onChange={(ids) => setForm((f) => ({ ...f, promotionIds: ids }))}
      />

      <div className="flex items-center gap-2 pt-0.5">
        <Button
          type="button"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          disabled={!isValid || createMutation.isPending}
          onClick={handleCreate}
        >
          {createMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Check className="h-3 w-3" />
          )}
          {createMutation.isPending ? t('stepStaticResources.creating') : t('stepStaticResources.createAndSelect')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={onCancel}
        >
          {t('stepStaticResources.cancel')}
        </Button>
      </div>
    </div>
  );
}

// ─── Inline line item card ──────────────────────────────────────────

function InlineLineItemCard({
  item,
  onQuantityChange,
  onPriceChange,
  onRemove,
}: {
  item: InlineLineItem;
  storeId: string;
  onQuantityChange: (quantity: number) => void;
  onPriceChange: (priceId: string, variantId: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('storefront');
  const { data: priceData } = usePrice(item.priceId, !!item.priceId);
  const { data: productData } = useProduct(item.productId, !!item.productId);

  const variant = productData?.variants?.find((v: any) => v.id === item.variantId);
  const imageUrl = variant?.imageUrl ?? null;
  const co = priceData?.currencyOptions;
  const firstCur = co ? Object.keys(co)[0] : null;
  const priceAmount = firstCur && co?.[firstCur] ? co[firstCur].amount : 0;

  const allPrices = React.useMemo(() => {
    if (!productData?.variants) return [];
    return productData.variants.flatMap((v: any) =>
      (v.prices ?? []).map((p: any) => ({ ...p, variantId: v.id, variantName: v.name })),
    );
  }, [productData]);

  const hasManyPrices = allPrices.length > 1;

  return (
    <div className="flex items-center gap-2 rounded-md border bg-background px-2 py-1.5">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <Package className="h-3 w-3 text-muted-foreground/40" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[10px] font-medium">{productData?.name || t('stepStaticResources.loading')}</p>
        {hasManyPrices ? (
          <Select
            value={item.priceId}
            onValueChange={(newPriceId) => {
              const selected = allPrices.find((p: any) => p.id === newPriceId);
              if (selected) onPriceChange(newPriceId, selected.variantId);
            }}
          >
            <SelectTrigger className="h-5 w-auto gap-1 border-none bg-transparent p-0 text-[9px] text-muted-foreground shadow-none hover:text-foreground [&>svg]:h-2.5 [&>svg]:w-2.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allPrices.map((p: any) => {
                const pCo = p.currencyOptions;
                const pCur = pCo ? Object.keys(pCo)[0] : null;
                const pAmt = pCur && pCo?.[pCur] ? pCo[pCur].amount : 0;
                const label = `${p.variantName}${pCur ? ` · ${formatMoney(pAmt, pCur.toUpperCase())}` : ''}`;
                return (
                  <SelectItem key={p.id} value={p.id} className="text-[10px]">
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        ) : (
          <p className="truncate text-[9px] text-muted-foreground">
            {variant?.name || ''}
            {firstCur ? ` · ${formatMoney(priceAmount, firstCur.toUpperCase())}` : ''}
          </p>
        )}
      </div>
      <Input
        type="number"
        min={1}
        value={item.quantity}
        onChange={(e) => onQuantityChange(parseInt(e.target.value) || 1)}
        className="h-6 w-12 shrink-0 px-1.5 text-center text-[10px]"
      />
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── Inline promotions picker ───────────────────────────────────────

function InlinePromotionsPicker({
  storeId,
  promotionIds,
  onChange,
}: {
  storeId: string;
  promotionIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation('storefront');
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-foreground"
      >
        <Tag className="h-3 w-3" />
        {t('stepStaticResources.discounts')}
        {promotionIds.length > 0 && (
          <Badge variant="secondary" className="ml-1 h-4 px-1 text-[9px]">
            {promotionIds.length}
          </Badge>
        )}
        <ChevronRight className={cn('h-3 w-3 transition-transform', isOpen && 'rotate-90')} />
      </button>

      {isOpen && (
        <div className="mt-1.5 space-y-1.5">
          <SearchPromotionInput
            storeId={storeId}
            excludeIds={promotionIds}
            onSelect={(promo) => {
              if (!promotionIds.includes(promo.id)) {
                onChange([...promotionIds, promo.id]);
              }
            }}
          />
          {promotionIds.length > 0 && (
            <div className="space-y-1">
              {promotionIds.map((pid) => (
                <InlinePromotionChip
                  key={pid}
                  promotionId={pid}
                  storeId={storeId}
                  onRemove={() => onChange(promotionIds.filter((id) => id !== pid))}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InlinePromotionChip({
  promotionId,
  storeId,
  onRemove,
}: {
  promotionId: string;
  storeId: string;
  onRemove: () => void;
}) {
  const { data: promo } = usePromotion(promotionId, storeId, !!promotionId && !!storeId);

  return (
    <div className="flex items-center gap-1.5 rounded border bg-background px-2 py-1">
      <Tag className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-[10px]">{promo?.name || promotionId}</span>
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

// ─── Multi-Offer List ────────────────────────────────────────────────
//
// Renders a list of selected offer cards (one per ID) plus an "Add offer"
// inline picker. Used for resources declared with `multiple: true` in a
// plugin manifest. Backward-compat with the single-offer flow is preserved
// by branching on `resource.multiple` in the main render — single-binding
// callers never reach this component.

function SelectedOfferCardLoader({
  offerId,
  onRemove,
}: {
  offerId: string;
  onRemove: () => void;
}) {
  const { t } = useTranslation('storefront');
  const { data, isLoading } = useOffer(offerId, !!offerId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border px-3 py-2">
        <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />
        <span className="text-muted-foreground text-xs">{t('stepStaticResources.loadingOffer')}</span>
      </div>
    );
  }

  if (!data?.offer) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-dashed px-3 py-2">
        <ImageOff className="text-muted-foreground h-3.5 w-3.5" />
        <span className="text-muted-foreground text-xs truncate">{offerId}</span>
        <button
          type="button"
          onClick={onRemove}
          className="text-muted-foreground hover:text-foreground ml-auto rounded-md p-1"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <SelectedOfferCard
      offer={data.offer}
      onClear={onRemove}
    />
  );
}

function MultiOfferList({
  values,
  onChange,
  storeId,
  min = 1,
  max,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  storeId: string;
  min?: number;
  max?: number;
}) {
  const { t } = useTranslation('storefront');
  const [showPicker, setShowPicker] = useState(false);
  // Id of the offer whose X was clicked while the list was already at `min`.
  // Removing it outright would persist a list the page cannot render, so the
  // picker opens in replace mode and the swap is committed as one change.
  const [replacingId, setReplacingId] = useState<string | null>(null);

  const canAddMore = max == null || values.length < max;
  const canRemove = canRemoveOffer(values, min);

  const handleAdd = (offerId: string) => {
    if (!offerId) return;
    if (values.includes(offerId)) {
      setShowPicker(false);
      return;
    }
    if (max != null && values.length >= max) return;
    onChange([...values, offerId]);
    setShowPicker(false);
  };

  const handleRemove = (offerId: string) => {
    if (!canRemove) {
      setShowPicker(false);
      setReplacingId(offerId);
      return;
    }
    onChange(removeOffer(values, offerId));
  };

  const handleReplace = (offerId: string) => {
    if (!offerId || !replacingId) return;
    if (offerId !== replacingId) {
      onChange(replaceOffer(values, replacingId, offerId));
    }
    setReplacingId(null);
  };

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="space-y-1.5">
          {values.map((id) =>
            id === replacingId ? (
              <div key={id} className="rounded-lg border border-dashed p-2">
                <p className="text-muted-foreground mb-1.5 text-[10px] font-medium">
                  {t('stepStaticResources.replacingOffer')}
                </p>
                <CompactOfferPicker
                  value=""
                  onChange={handleReplace}
                  storeId={storeId}
                  excludeIds={values.filter((other) => other !== id)}
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReplacingId(null)}
                    className="h-7 text-xs"
                  >
                    {t('stepStaticResources.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <SelectedOfferCardLoader
                key={id}
                offerId={id}
                onRemove={() => handleRemove(id)}
              />
            ),
          )}
        </div>
      )}

      {replacingId ? null : showPicker ? (
        <div className="rounded-lg border border-dashed p-2">
          <CompactOfferPicker
            value=""
            onChange={handleAdd}
            storeId={storeId}
            excludeIds={values}
          />
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowPicker(false)}
              className="h-7 text-xs"
            >
              {t('stepStaticResources.cancel')}
            </Button>
          </div>
        </div>
      ) : canAddMore ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowPicker(true)}
          className="h-8 w-full gap-1 text-xs"
        >
          <Plus className="h-3 w-3" />
          {t('stepStaticResources.addOffer')}
        </Button>
      ) : (
        <p className="text-muted-foreground text-[10px] text-center">
          {t('stepStaticResources.maxOffersReached', { max })}
        </p>
      )}

      {values.length < min && (
        <p className="text-amber-600 text-[10px]">
          {t('stepStaticResources.minOffersRequired', { count: min })}
        </p>
      )}
    </div>
  );
}

export function StepStaticResourcesEditor({
  stepId,
  variantId,
  funnelId,
  instanceId,
  storeId,
  pagePath,
  staticResources = {},
  onResourceChange = () => {},
}: StepStaticResourcesEditorProps) {
  const { t } = useTranslation('storefront');
  const [localResources, setLocalResources] = useState<Record<string, any>>({});

  // Fetch current static resources from database (for initial display)
  const { data: currentStaticResources } = trpc.funnels.getStepStaticResources.useQuery(
    { funnelId, stepId, variantId, storeId } as any,
    { enabled: !!funnelId && !!stepId && !!storeId },
  );

  // Initialize from props OR fetched data (props take priority for immediate display)
  useEffect(() => {
    // Priority: 1) prop staticResources, 2) fetched currentStaticResources
    const initialResources =
      Object.keys(staticResources).length > 0 ? staticResources : currentStaticResources || {};

    setLocalResources(initialResources);
  }, [stepId, variantId, staticResources, currentStaticResources]);

  // Use local state for display (provides immediate visual feedback)
  const displayedResources = localResources;

  // @ts-ignore - TRPC types need regeneration (restart dev server)
  const { data: instance, isLoading: instanceLoading } = trpc.plugins.getInstanceMetadata.useQuery(
    { instanceId: instanceId! },
    {
      enabled: !!instanceId,
      staleTime: 60 * 1000,
    },
  );

  // Get pages from deployment (the manifest pages are stored directly in deployment.pages)
  const pages = instance?.deployment?.pages;

  // Create a manifest-like structure for the parser
  const manifest = pages ? { pages } : null;

  // Parse static resources from manifest (filtered by current page)
  const staticResourcesList = useMemo(() => {
    if (!manifest || !pagePath) {
      return [];
    }

    // Funnel config stores routing-templated paths like `/offer/:orderId`,
    // but the deployed manifest stores bare content paths like `/offer`
    // (StudioAutoDeployFrame.defaultPagePath strips params on purpose —
    // the manifest page = where the bundled HTML lives, the `:param` is
    // a routing concern). Exact-match fails for any page whose route uses
    // a templated path. Fall back to a param-stripped match so the offer
    // picker (and any other static-resource UI) finds its page.
    const stripParams = (p: string) => p.replace(/\/:[^/]+/g, '');
    const pageMatch = (p: any) =>
      p.path === pagePath || stripParams(p.path || '') === stripParams(pagePath);
    const page = pages?.find(pageMatch);

    if (!page) {
      return [];
    }

    // Parse resources only for this specific page
    const resources = parseManifestRequirements({ pages: [page] });
    return resources;
  }, [manifest, instance, pagePath, pages]);

  // Handle resource change - update local state for immediate feedback.
  // Single-binding values are stored as `{id: string}` (existing convention);
  // multi-binding values are stored as `string[]` so the funnel persists the
  // full ordered list. The parent caller already accepts both shapes.
  const handleResourceChange = (resource: StaticResource, value: string | string[]) => {
    const resourceValue: { id: string } | string[] = Array.isArray(value)
      ? value
      : { id: value };

    setLocalResources((prev) => ({
      ...prev,
      [resource.resource]: resourceValue,
    }));

    onResourceChange(resource.resource, resourceValue);
  };

  if (!instanceId) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-6 text-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
          <Database className="text-muted-foreground h-4 w-4" />
        </div>
        <p className="text-xs font-medium">{t('stepStaticResources.noDesignSelected')}</p>
      </div>
    );
  }

  if (instanceLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  }

  // If no static resources from manifest, show empty state
  const resourcesToDisplay = staticResourcesList;

  if (staticResourcesList.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {resourcesToDisplay.map((resource) => {
        const resourceData = displayedResources[resource.resource];
        // Three legacy shapes funnel data may show up as: bare string id,
        // `{id: string}` object, or `string[]` (only when `multiple: true`).
        // Single-binding code paths still get a plain string via currentValue.
        const isMultiArray = Array.isArray(resourceData);
        const currentValue =
          isMultiArray
            ? ''
            : typeof resourceData === 'string'
              ? resourceData
              : resourceData?.id || '';
        const currentValues: string[] = isMultiArray
          ? (resourceData as string[]).filter((v): v is string => typeof v === 'string' && !!v)
          : currentValue
            ? [currentValue]
            : [];
        const isOffer = resource.type === 'offer';
        // Offer resources are always rendered with the multi-card picker —
        // the manifest's `multiple` flag is ignored for offers so funnel
        // admins can add/remove packs on every step without the page
        // having to opt in. Legacy single-string bindings still display
        // as a 1-element list (see currentValues fallback above).
        const isMulti = isOffer;

        return (
          <div key={resource.resource}>
            {!isOffer && (
              <div className="mb-1.5">
                <Label className="text-xs font-medium">
                  {resource.label}
                  {resource.required && <span className="ml-1 text-red-500">*</span>}
                </Label>
                {resource.description && (
                  <p className="text-muted-foreground mt-0.5 text-[10px]">{resource.description}</p>
                )}
              </div>
            )}

            {resource.type === 'product' && (
              <ProductSelector
                value={currentValue}
                onChange={(value) => handleResourceChange(resource, value)}
                storeId={storeId}
                searchable={true}
                placeholder={t('stepStaticResources.searchSelectProduct')}
              />
            )}

            {resource.type === 'variant' && (
              <VariantSelector
                value={currentValue}
                onChange={(value) => handleResourceChange(resource, value)}
                storeId={storeId}
              />
            )}

            {resource.type === 'price' && (
              <PriceSelector
                value={currentValue}
                onChange={(value) => handleResourceChange(resource, value)}
                storeId={storeId}
              />
            )}

            {isOffer && !isMulti && (
              <CompactOfferPicker
                value={currentValue}
                onChange={(value) => handleResourceChange(resource, value)}
                storeId={storeId}
              />
            )}

            {isOffer && isMulti && (
              <MultiOfferList
                values={currentValues}
                onChange={(next) => handleResourceChange(resource, next)}
                storeId={storeId}
                min={resource.min}
                max={resource.max}
              />
            )}

            {resource.type === 'orderBump' && (
              <UpsellsProvider storeId={storeId}>
                <OrderBumpsProvider>
                  <OrderBumpSelector
                    value={currentValue}
                    onChange={(value) => handleResourceChange(resource, value)}
                    storeId={storeId}
                  />
                </OrderBumpsProvider>
              </UpsellsProvider>
            )}
          </div>
        );
      })}
    </div>
  );
}
